import React, { useState } from 'react';
import { useClientPortal } from '../contexts/ClientPortalContext';
import { createQuotationZoho, sendQuotationNotification } from '../services/quotationService';

const OrderSummaryModal = ({ cart, user, onClose, onConfirm }) => {
    const { updateQuantity, removeFromCart, clearCartAfterSuccess } = useClientPortal();
    const [isConfirming, setIsConfirming] = useState(false);

    const calculateTotal = () => {
        return cart.reduce((total, item) => {
            const price = item.product.price || 0;
            return total + (price * item.quantity);
        }, 0).toFixed(2);
    };

    const handleQuantityChange = (productId, newQuantity) => {
        const quantity = parseInt(newQuantity) || 0;
        if (quantity <= 0) {
            removeFromCart(productId);
        } else {
            updateQuantity(productId, quantity);
        }
    };

    const handleConfirmOrder = async () => {
        setIsConfirming(true);
        try {
            // Extract customer ID from contact_id (structure from PostgreSQL backend)
            const customerId = user.contact_id;
            
            // 🔍 DEBUG: Vérifier les données avant l'appel// Create quotation in Odoo
            const quotationResult = await createQuotationZoho(
                customerId, 
                cart, 
                user
            );

            if (quotationResult.success) {
                // Send notification email
                try {
                    await sendQuotationNotification(
                        quotationResult,
                        user,
                        cart
                    );} catch (emailError) {
                    console.warn('⚠️ Quotation created but email notification failed:', emailError);
                }

                // ✨ NOUVEAU: Supprimer le panier de PostgreSQL après envoi réussi
                try {
                    await clearCartAfterSuccess('quotation_sent');} catch (clearError) {
                    console.warn('⚠️ Quotation sent but cart cleanup failed:', clearError);
                    // Ne pas bloquer l'UI pour ce problème
                }

                // Show success message
                alert(`🎉 Order submitted successfully!\n\n📋 Quotation: ${quotationResult.quotationNumber}\n📧 You will receive a detailed quotation within 24-48 hours.\n\nThank you for your business!`);
                
                onConfirm();
            } else {
                throw new Error('Failed to create quotation');
            }
        } catch (error) {
            console.error('❌ Error submitting order:', error);
            alert(`❌ Error submitting order: ${error.message}\n\nPlease try again or contact support.`);
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
            }}>
                <div style={{
                    textAlign: 'center',
                    marginBottom: '30px'
                }}>
                    <h2 style={{ 
                        color: '#333', 
                        marginBottom: '10px',
                        fontSize: '28px'
                    }}>
                        📋 Order Summary
                    </h2>
                    <p style={{ color: '#666', margin: 0 }}>
                        Review your order before submitting
                    </p>
                </div>

                {/* Informations client */}
                <div style={{
                    background: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}>
                    <strong>Customer:</strong> {user.name || user.email}<br />
                    <strong>Email:</strong> {user.email}
                </div>

                {/* Articles de la commande */}
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: '#333', marginBottom: '15px' }}>Order Items:</h3>
                    
                    {cart.map((item, index) => (
                        <div key={item.product.item_id || item.product.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '15px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: '10px'
                        }}>
                            <img
                                src={item.product.image || "/images/placeholder.png"}
                                alt={item.product.name}
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    marginRight: '15px'
                                }}
                            />
                            
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                                    {item.product.name}
                                </h4>
                                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                    {item.product.price ? `€${item.product.price} each` : 'Price on request'}
                                </p>
                            </div>
                            
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px',
                                marginRight: '15px'
                            }}>
                                <label style={{ fontSize: '14px', color: '#666' }}>Qty:</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleQuantityChange(item.product.item_id || item.product.id, e.target.value)}
                                    style={{
                                        width: '60px',
                                        padding: '5px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        textAlign: 'center'
                                    }}
                                />
                            </div>
                            
                            <div style={{ 
                                minWidth: '80px', 
                                textAlign: 'right',
                                marginRight: '15px'
                            }}>
                                {item.product.price ? (
                                    <strong>€{(item.product.price * item.quantity).toFixed(2)}</strong>
                                ) : (
                                    <span style={{ color: '#666' }}>TBD</span>
                                )}
                            </div>
                            
                            <button
                                onClick={() => removeFromCart(item.product.item_id || item.product.id)}
                                style={{
                                    background: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '8px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                                title="Remove from cart"
                            >
                                🗑️
                            </button>
                        </div>
                    ))}
                </div>

                {/* Total */}
                <div style={{
                    background: '#e8f5e8',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    textAlign: 'right'
                }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                        Estimated Total: €{calculateTotal()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        *Final prices will be confirmed in the quotation
                    </div>
                </div>

                {/* Boutons d'action */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        disabled={isConfirming}
                        style={{
                            padding: '12px 24px',
                            border: '2px solid #e0e0e0',
                            background: 'white',
                            color: '#666',
                            borderRadius: '8px',
                            cursor: isConfirming ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '600'
                        }}
                    >
                        📝 Continue Editing
                    </button>
                    
                    <button
                        onClick={handleConfirmOrder}
                        disabled={isConfirming || cart.length === 0}
                        style={{
                            padding: '12px 24px',
                            border: 'none',
                            background: isConfirming ? '#ccc' : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            cursor: (isConfirming || cart.length === 0) ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            minWidth: '160px'
                        }}
                    >
                        {isConfirming ? '⏳ Submitting...' : '✅ Submit Order'}
                    </button>
                </div>

                <div style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    padding: '15px',
                    background: '#fff3cd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#856404'
                }}>
                    💡 <strong>Note:</strong> This will create a quotation request in our system. 
                    You will receive a detailed quote with final pricing and availability.
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryModal;
